import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* The service answers with a REAL HTTP status and a { code, message } body, so
   axios rejects and the sentence is read off the error — never off a status
   field in a 200 body. A bare refusal ({ code: "forbidden" }) carries no
   message at all, which is why the code is the second thing tried. */
const errText = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

export const fetchMillingSlots = createAsyncThunk(
  "millingSlot/fetchAll",
  async ({ page = 1, limit = 10, upcoming = false, millType = "", storeId = "" } = {}, thunkAPI) => {
    try {
      const res = await api.get(`/admin/milling/slots?page=${page}&limit=${limit}${upcoming ? "&upcoming=true" : ""}${millType ? `&millType=${millType}` : ""}${storeId ? `&storeId=${encodeURIComponent(storeId)}` : ""}`);
      /* the payload IS { data, pagination } — that inner `data` is the page of
         slots, not a leftover Node wrapper */
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to fetch slots"));
    }
  }
);

export const createMillingSlot = createAsyncThunk(
  "millingSlot/create",
  async (data, thunkAPI) => {
    try {
      const res = await api.post("/admin/milling/slots", data);
      /* 201 { message, data: slot } — the write routes carry their own
         top-level `data`, so this stays a double read */
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to create"));
    }
  }
);

export const bulkCreateMillingSlots = createAsyncThunk(
  "millingSlot/bulkCreate",
  async (data, thunkAPI) => {
    try {
      const res = await api.post("/admin/milling/slots/bulk", data);
      /* the whole { message, data: [slots] } — the screen shows the message,
         which counts the duplicates the server skipped */
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to create slots"));
    }
  }
);

export const updateMillingSlot = createAsyncThunk(
  "millingSlot/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await api.put(`/admin/milling/slots/${id}`, data);
      return res.data.data; // { message, data: slot }
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to update"));
    }
  }
);

export const toggleMillingSlotStatus = createAsyncThunk(
  "millingSlot/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/admin/milling/slots/${id}/status`);
      return res.data.data; // { message, data: slot }
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to update status"));
    }
  }
);

export const deleteMillingSlot = createAsyncThunk(
  "millingSlot/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/admin/milling/slots/${id}`);
      return id;
    } catch (err) {
      /* 400 slot_booked names the fix ("Disable it instead of deleting") —
         surface the server's sentence rather than a generic failure */
      return thunkAPI.rejectWithValue(errText(err, "Delete failed"));
    }
  }
);

const millingSlotSlice = createSlice({
  name: "millingSlot",
  initialState: {
    slots: [],
    loading: false,
    error: null,
    pagination: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMillingSlots.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMillingSlots.fulfilled, (state, action) => {
        state.loading = false;
        state.slots = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchMillingSlots.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createMillingSlot.fulfilled, (state, action) => {
        state.pagination.total = (state.pagination.total || 0) + 1;
      })
      .addCase(createMillingSlot.rejected, (state, action) => { state.error = action.payload; })

      .addCase(bulkCreateMillingSlots.fulfilled, (state, action) => {
        state.pagination.total = (state.pagination.total || 0) + (action.payload.data?.length || 0);
      })
      .addCase(bulkCreateMillingSlots.rejected, (state, action) => { state.error = action.payload; })

      /* a slot is keyed by `id` (a bigint) now — it has no _id at all */
      .addCase(updateMillingSlot.fulfilled, (state, action) => {
        const index = state.slots.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) state.slots[index] = action.payload;
      })
      .addCase(updateMillingSlot.rejected, (state, action) => { state.error = action.payload; })

      .addCase(toggleMillingSlotStatus.fulfilled, (state, action) => {
        const index = state.slots.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) state.slots[index] = action.payload;
      })

      .addCase(deleteMillingSlot.fulfilled, (state, action) => {
        /* the window is gone — drop it from the page as well as off the count,
           or the list keeps rendering a slot the server no longer has. The id
           came from the client, so it is compared as text: a bigint arrives
           from the server as a number and could be handed back as either. */
        state.slots = state.slots.filter((s) => String(s.id) !== String(action.payload));
        state.pagination.total = (state.pagination.total || 1) - 1;
      })
      .addCase(deleteMillingSlot.rejected, (state, action) => { state.error = action.payload; });
  },
});

export default millingSlotSlice.reducer;
